import type React from "react";
import {
  composeRenderProps,
  Focusable,
  Button as RACButton,
  type ButtonProps as RACButtonProps,
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import { Loader } from "@/components/ui/loader";
import { Tooltip } from "@/components/ui/tooltip";
import {
  cn,
  composeTailwindRenderProps,
  focusRing,
} from "@/components/ui/utils";

export const buttonVariants = tv({
  extend: focusRing,
  base: [
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:opacity-50 [&_svg]:text-current [&_svg]:stroke-current [&_svg:not([class*=size-])]:size-3.5 ",
  ],
  variants: {
    variant: {
      default:
        "bg-primary text-primary-foreground shadow-xs shadow-black/5 hover:bg-primary/90",
      destructive:
        "bg-destructive text-destructive-foreground shadow-xs shadow-black/5 hover:bg-destructive/90",
      outline:
        "border border-input bg-background shadow-xs shadow-black/5 hover:bg-accent hover:text-accent-foreground",
      secondary:
        "bg-secondary text-secondary-foreground shadow-xs shadow-black/5 hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline",
    },
    isPending: {
      true: "[&_svg:nth-child(2)]:hidden",
    },
    size: {
      default: "h-8 px-3 [&_svg]:first:me-1.5 [&_svg]:-ms-1",
      sm: "h-6 px-2 text-xs rounded-md [&_svg]:me-1.5 [&_svg:not([class*=size-])]:size-3",
      lg: "h-10 rounded-lg px-8",
      icon: "h-8 w-8 [&_svg]:me-0 [&_svg]:-ms-0 [&_svg]:opacity-100",
      "icon-sm":
        "h-6 w-6 [&_svg]:me-0 [&_svg]:-ms-0 rounded-md [&_svg]:opacity-100 [&_svg:not([class*=size-])]:size-3.5",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export type ButtonProps = RACButtonProps &
  VariantProps<typeof buttonVariants> & {
    ref?: React.Ref<HTMLButtonElement>;
  };

export function Button({
  className,
  variant,
  size,
  ref,
  children,
  ...props
}: ButtonProps) {
  return (
    <RACButton
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        className,
        buttonVariants({ variant, size, isPending: props.isPending }),
      )}
    >
      {composeRenderProps(children, (children, { isPending }) => (
        <>
          {isPending && <Loader />}
          {children}
        </>
      ))}
    </RACButton>
  );
}

export function ButtonGroup({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center",
        "[&>*:first-child]:rounded-r-none",
        "[&>*:last-child]:rounded-l-none",
        "[&>*:not(:first-child):not(:last-child)]:rounded-none",
        "[&>*:not(:last-child)]:border-r-0",
        // Default button has not border in dark mode
        "dark:[&>*:not(.bg-transparent)]:border",
        className,
      )}
      {...props}
    />
  );
}

export function ToggleButton(
  props: RACToggleButtonProps &
    VariantProps<typeof buttonVariants> & {
      tooltip?: React.ReactNode;
      allowTooltipOnDisabled?: boolean;
    },
) {
  const { variant, tooltip, allowTooltipOnDisabled, size, ...buttonProps } =
    props;

  const toggleButton = (
    <RACToggleButton
      {...buttonProps}
      data-variant={variant}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => {
          return buttonVariants({ variant, size, className, ...renderProps });
        },
      )}
    />
  );

  if (tooltip) {
    if (allowTooltipOnDisabled && buttonProps.isDisabled) {
      return (
        <Tooltip>
          <Focusable>
            {/** biome-ignore lint/a11y/useFocusableInteractive: yes it is */}
            <div className="content" role="button">
              {toggleButton}
            </div>
          </Focusable>
          {tooltip}
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        {toggleButton}
        {tooltip}
      </Tooltip>
    );
  }

  return toggleButton;
}

export { ButtonContext } from "react-aria-components";
